import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LogIn } from 'lucide-react';

import { PageSeo } from '../components/PageSeo';
import { PasswordField } from '../components/PasswordField';
import { SubpageLayout } from '../components/SubpageLayout';
import { useAuth } from '../context/AuthContext';

export function LoginPage() {
    const { t } = useTranslation();
    const { login, authMode } = useAuth();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success(t('loginPage.toastSuccess'), {
                autoClose: 2800,
            });
            window.setTimeout(() => {
                navigate('/', { replace: true });
            }, 600);
        } catch (err) {
            const msg =
                err && typeof err === 'object' && 'message' in err
                    ? String(/** @type {{ message?: unknown }} */ (err).message)
                    : '';
            setError(msg || t('loginPage.errorFallback'));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            <PageSeo
                title={`${t('loginPage.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.login')}
                noindex
            />
            <SubpageLayout
                title={t('loginPage.title')}
                subtitle={t('loginPage.subtitle')}
                contentClassName="login-page"
            >
            <div className="login-wrap">
                <div className="login-card">
                    <div className="login-card-icon-wrap" aria-hidden>
                        <LogIn
                            size={22}
                            strokeWidth={1.85}
                            className="login-card-icon"
                        />
                    </div>
                    {authMode === 'simulation' ? (
                        <p className="login-demo-note mb-4">
                            <strong>{t('loginPage.simulationLabel')}</strong>{' '}
                            {t('loginPage.simulationBody')}
                        </p>
                    ) : null}
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="mb-3">
                            <label
                                htmlFor="login-email"
                                className="form-label login-field-label"
                            >
                                {t('loginPage.emailLabel')}
                            </label>
                            <input
                                id="login-email"
                                type="email"
                                name="email"
                                className="form-control login-field-input"
                                autoComplete="username"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <PasswordField
                            id="login-password"
                            name="password"
                            label={t('loginPage.passwordLabel')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            disabled={submitting}
                        />
                        {error ? (
                            <div
                                className="login-alert login-alert-error"
                                role="alert"
                            >
                                {error}
                            </div>
                        ) : null}
                        <button
                            type="submit"
                            className="btn login-submit-btn w-100"
                            disabled={submitting}
                        >
                            {submitting
                                ? t('loginPage.submitLoading')
                                : t('loginPage.submit')}
                        </button>
                    </form>
                    <p className="login-back mt-4 mb-0 text-center">
                        <Link to="/" className="login-back-link">
                            {t('loginPage.backHome')}
                        </Link>
                    </p>
                </div>
            </div>
        </SubpageLayout>
        </>
    );
}
